/**
 * QuickEntry.jsx
 * Excel-style unified entry grid for Sales, Expenses, Payments, and Stock.
 * Uses the same backend APIs as the individual form pages.
 * Does NOT affect any existing component.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { buildApiUrl } from '../config';
import './QuickEntry.css';

// ─── Column definitions ────────────────────────────────────────────────────────
// Each column has an id, a short label, a width hint, and which entry types use it.
const COLUMNS = [
  { id: 'entryType',      label: 'Type',             cls: 'col-type',    usedBy: ['sales','expenses','payments','stock'] },
  { id: 'subType',        label: 'Sub-Type',          cls: 'col-subtype', usedBy: ['sales','expenses','payments','stock'] },
  { id: 'service',        label: 'Service',           cls: 'col-std',     usedBy: ['sales'] },
  { id: 'vehicleNo',      label: 'Vehicle No',        cls: 'col-std',     usedBy: ['sales','stock'] },
  { id: 'vehicleType',    label: 'Vehicle Type',      cls: 'col-std',     usedBy: ['sales','stock'] },
  { id: 'customerCompany',label: 'Customer / Company',cls: 'col-wide',    usedBy: ['sales','payments','stock'] },
  { id: 'contactNo',      label: 'Contact No',        cls: 'col-std',     usedBy: ['sales'] },
  { id: 'driverName',     label: 'Driver Name',       cls: 'col-std',     usedBy: ['sales'] },
  { id: 'driverNo',       label: 'Driver No',         cls: 'col-std',     usedBy: ['sales'] },
  { id: 'trnNo',          label: 'TRN No',            cls: 'col-std',     usedBy: ['sales'] },
  { id: 'commission',     label: 'Commission',        cls: 'col-std',     usedBy: ['sales'] },
  { id: 'paymentMethod',  label: 'Payment Method',    cls: 'col-std',     usedBy: ['sales','expenses','payments','stock'] },
  { id: 'amount',         label: 'Amount (AED)',       cls: 'col-std',     usedBy: ['sales','expenses','payments','stock'] },
  { id: 'expenseType',    label: 'Expense Type',      cls: 'col-std',     usedBy: ['expenses'] },
  { id: 'supplier',       label: 'Supplier',          cls: 'col-std',     usedBy: ['expenses','stock'] },
  { id: 'workerName',     label: 'Worker Name',       cls: 'col-std',     usedBy: ['expenses'] },
  { id: 'stockType',      label: 'Stock Category',    cls: 'col-std',     usedBy: ['stock'] },
  { id: 'brand',          label: 'Brand',             cls: 'col-std',     usedBy: ['stock'] },
  { id: 'itemType',       label: 'Oil Type / Filter', cls: 'col-std',     usedBy: ['stock'] },
  { id: 'quantity',       label: 'Quantity',          cls: 'col-std',     usedBy: ['stock'] },
  { id: 'description',    label: 'Description',       cls: 'col-notes',   usedBy: ['expenses','stock'] },
  { id: 'notes',          label: 'Notes',             cls: 'col-notes',   usedBy: ['sales','payments','stock'] },
];

// Which columns are relevant for each entry type
const RELEVANT = {
  sales_walkin:    ['entryType','subType','service','vehicleNo','vehicleType','customerCompany','contactNo','commission','paymentMethod','amount','notes'],
  sales_credit:    ['entryType','subType','service','vehicleNo','vehicleType','customerCompany','contactNo','driverName','driverNo','trnNo','commission','paymentMethod','amount','notes'],
  expenses_expense:['entryType','subType','expenseType','supplier','paymentMethod','amount','description'],
  expenses_overtime:['entryType','subType','workerName','amount','description'],
  payments_company: ['entryType','subType','customerCompany','paymentMethod','amount','notes'],
  payments_supplier:['entryType','subType','supplier','paymentMethod','amount','notes'],
  payments_walkin:  ['entryType','subType','customerCompany','paymentMethod','amount','notes'],
  payments_overtime:['entryType','subType','workerName','paymentMethod','amount','notes'],
  stock_addStock:   ['entryType','subType','stockType','brand','itemType','quantity','amount','supplier','description'],
  stock_sellCash:   ['entryType','subType','stockType','brand','itemType','quantity','amount','vehicleNo','vehicleType','customerCompany','contactNo','paymentMethod','notes'],
  stock_sellCredit: ['entryType','subType','stockType','brand','itemType','quantity','amount','vehicleNo','vehicleType','customerCompany','contactNo','driverName','driverNo','paymentMethod','notes'],
};

function getRelevantColumns(entryType, subType) {
  if (!entryType || !subType) return null;
  return RELEVANT[`${entryType}_${subType}`] || null;
}

// API stores walk-in payments as entity_type 'customer'
function normalizeSubType(entryType, rawSubType) {
  if (entryType === 'payments' && rawSubType === 'customer') return 'walkin';
  return rawSubType;
}

function supplierLabel(s) {
  if (typeof s === 'string') return s;
  return s.name || s.SupplierName || `Supplier #${s.id}`;
}

function supplierValue(s) {
  if (typeof s === 'string') return s;
  return s.name || s.SupplierName || String(s.id);
}

function isRelevant(colId, entryType, subType) {
  // Until sub-type is chosen, only type / sub-type columns are editable
  if (!entryType || !subType) {
    return colId === 'entryType' || colId === 'subType';
  }

  const cols = getRelevantColumns(entryType, subType);
  if (!cols) return false;
  return cols.includes(colId);
}

function resetOtherFlags() {
  return {
    _otherService: false,
    _otherVehicleType: false,
    _otherCompany: false,
    _otherExpenseType: false,
    _otherSupplier: false,
    _otherWorker: false,
    _otherBrand: false,
    _otherItemType: false,
    _selectedCompanyId: '',
  };
}

// ─── Blank row factory ─────────────────────────────────────────────────────────
function blankRow(id) {
  return {
    _id: id,
    _saved: false,
    _saving: false,
    _error: null,
    entryType: '',
    subType: '',
    ...resetOtherFlags(),
    // sales
    service: '',
    vehicleNo: '',
    vehicleType: '',
    customerCompany: '',
    contactNo: '',
    driverName: '',
    driverNo: '',
    trnNo: '',
    commission: '',
    // expenses
    expenseType: '',
    supplier: '',
    workerName: '',
    description: '',
    // payments
    // stock
    stockType: '',
    brand: '',
    itemType: '',
    quantity: '',
    // common
    paymentMethod: '',
    amount: '',
    notes: '',
  };
}

let _rowCounter = 1000;
function nextId() { return ++_rowCounter; }

// ─── Main Component ────────────────────────────────────────────────────────────
const QuickEntry = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);
  const [rows, setRows] = useState([blankRow(nextId())]);
  const [savedRows, setSavedRows] = useState([]);    // fetched from server
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(-1);
  const containerRef = useRef(null);

  // dropdown data
  const [dropdowns, setDropdowns] = useState({
    services: [],
    expenseTypes: [],
    expenseSuppliers: [],
    companies: [],
    stockSuppliers: [],
    vehicleTypes: [],
    workers: [],
    walkinCustomers: [],
  });
  const [stockBrands, setStockBrands] = useState({ Oil: [], Filter: [] });
  const [stockTypes, setStockTypes] = useState({ Oil: [], Filter: [] });
  const [companiesInfo, setCompaniesInfo] = useState([]);

  // Prevent page-level scroll while Quick Entry is open (table scrolls internally)
  useEffect(() => {
    document.documentElement.classList.add('quick-entry-active');
    document.body.classList.add('quick-entry-active');
    return () => {
      document.documentElement.classList.remove('quick-entry-active');
      document.body.classList.remove('quick-entry-active');
    };
  }, []);

  // ── Load dropdowns once ─────────────────────────────────────────────────────
  useEffect(() => {
    fetch(buildApiUrl('api/quick-entry/dropdowns'))
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          // Merge into existing state shape to keep safe defaults
          setDropdowns(prev => ({
            ...prev,
            services:         Array.isArray(data.services)         ? data.services         : [],
            expenseTypes:     Array.isArray(data.expenseTypes)     ? data.expenseTypes     : [],
            expenseSuppliers: Array.isArray(data.expenseSuppliers) ? data.expenseSuppliers : [],
            companies:        Array.isArray(data.companies)        ? data.companies        : [],
            stockSuppliers:   Array.isArray(data.stockSuppliers)   ? data.stockSuppliers   : [],
            vehicleTypes:     Array.isArray(data.vehicleTypes)     ? data.vehicleTypes     : [],
            workers:          Array.isArray(data.workers)          ? data.workers          : [],
            walkinCustomers:  Array.isArray(data.walkinCustomers)  ? data.walkinCustomers  : [],
          }));
        }
      })
      .catch(err => console.error('Error loading dropdowns:', err));

    fetch(buildApiUrl('api/companiesinfo'))
      .then(r => r.json())
      .then(data => setCompaniesInfo(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error loading company details:', err));

    // Pre-load stock options for both categories
    ['Oil', 'Filter'].forEach(cat => {
      fetch(buildApiUrl(`api/stock/options?stockType=${cat}`))
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setStockBrands(prev => ({ ...prev, [cat]: data.brands || [] }));
            setStockTypes(prev => ({ ...prev, [cat]: data.types || [] }));
          }
        })
        .catch(() => {});
    });
  }, []);

  // ── Load saved entries when date changes ────────────────────────────────────
  const loadEntries = useCallback((date) => {
    if (!date) return Promise.resolve();
    setLoading(true);
    return fetch(buildApiUrl(`api/quick-entry/entries?date=${date}`))
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setSavedRows(data.data || []);
          setSelectedRow(-1);
        } else {
          setSavedRows([]);
        }
      })
      .catch(err => {
        console.error('QuickEntry loadEntries error:', err);
        setSavedRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadEntries(selectedDate);
    setSelectedRow(-1);
    // Reset entry rows when date changes
    setRows([blankRow(nextId())]);
  }, [selectedDate, loadEntries]);

  useEffect(() => {
    if (selectedRow < 0 || !containerRef.current) return;
    const savedTrs = containerRef.current.querySelectorAll('tbody tr.qe-row-saved');
    if (savedTrs[selectedRow]) {
      savedTrs[selectedRow].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedRow]);

  const colIndex = (colId) => COLUMNS.findIndex(c => c.id === colId);

  const withNav = (rowId, colId, extra = {}) => ({
    ...extra,
    className: ['qe-grid-focusable', extra.className].filter(Boolean).join(' '),
    'data-qe-row': String(rowId),
    'data-qe-col': String(colIndex(colId)),
  });

  const getFocusableElements = () => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll(
        '.qe-grid-focusable:not([disabled]):not([readonly])'
      )
    );
  };

  const shouldMoveOnHorizontalArrow = (el, key) => {
    if (el.tagName !== 'INPUT') return true;
    if (el.type === 'number') return true;
    const len = el.value?.length ?? 0;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (key === 'ArrowLeft') return start === 0 && end === 0;
    if (key === 'ArrowRight') return start === len && end === len;
    return true;
  };

  const handleTableKeyDown = (e) => {
    const { key } = e;
    const active = document.activeElement;

    if (['ArrowUp', 'ArrowDown'].includes(key) && !active?.dataset?.qeCol) {
      if (!savedRows.length) return;
      e.preventDefault();
      if (key === 'ArrowDown') {
        setSelectedRow(prev => (prev < 0 ? 0 : Math.min(prev + 1, savedRows.length - 1)));
      } else {
        setSelectedRow(prev => Math.max(prev - 1, 0));
      }
      return;
    }

    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
    if (!active?.dataset?.qeRow) return;

    if (['ArrowLeft', 'ArrowRight'].includes(key) && !shouldMoveOnHorizontalArrow(active, key)) {
      return;
    }

    const focusables = getFocusableElements();
    const currentIdx = focusables.indexOf(active);
    if (currentIdx === -1) return;

    e.preventDefault();

    if (key === 'ArrowLeft' && currentIdx > 0) {
      focusables[currentIdx - 1].focus();
      return;
    }
    if (key === 'ArrowRight' && currentIdx < focusables.length - 1) {
      focusables[currentIdx + 1].focus();
      return;
    }

    const rowKey = active.dataset.qeRow;
    const colKey = active.dataset.qeCol;
    const rowKeys = [...new Set(focusables.map(el => el.dataset.qeRow))];
    const rowIdx = rowKeys.indexOf(rowKey);

    if (key === 'ArrowUp' && rowIdx > 0) {
      const target = focusables.find(
        el => el.dataset.qeRow === rowKeys[rowIdx - 1] && el.dataset.qeCol === colKey
      );
      target?.focus();
      return;
    }
    if (key === 'ArrowDown' && rowIdx < rowKeys.length - 1) {
      const target = focusables.find(
        el => el.dataset.qeRow === rowKeys[rowIdx + 1] && el.dataset.qeCol === colKey
      );
      target?.focus();
    }
  };

  // ── Row field update helper ─────────────────────────────────────────────────
  const patchRow = (id, updates) => {
    setRows(prev => prev.map(row => (row._id !== id ? row : { ...row, ...updates })));
  };

  const findCompany = (id) => companiesInfo.find(c => String(c.id) === String(id));

  const applyCompanyAutofill = (company) => ({
    contactNo: company?.contactNo || '',
    trnNo: company?.trnNo || '',
    driverName: company?.contactPerson || '',
  });

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(row => {
      if (row._id !== id) return row;
      const updated = { ...row, [field]: value };
      if (field === 'entryType') {
        updated.subType = '';
        updated._entityId = undefined;
        Object.assign(updated, resetOtherFlags());
      }
      if (field === 'subType') {
        updated.customerCompany = '';
        updated.supplier = '';
        updated.workerName = '';
        updated.contactNo = '';
        updated.trnNo = '';
        updated._entityId = undefined;
        Object.assign(updated, resetOtherFlags());
      }
      if (field === 'stockType') {
        updated.brand = '';
        updated.itemType = '';
        updated._otherBrand = false;
        updated._otherItemType = false;
      }
      return updated;
    }));
  };

  // ── Add empty row ───────────────────────────────────────────────────────────
  const addRow = () => {
    setRows(prev => [...prev, blankRow(nextId())]);
  };

  // ── Save a row ──────────────────────────────────────────────────────────────
  const saveRow = async (row) => {
    if (!row.entryType || !row.subType) {
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, _error: 'Select Type and Sub-Type first.' } : r));
      return;
    }

    // Client-side validation before hitting the server
    const validationError = validateRow(row);
    if (validationError) {
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, _error: validationError } : r));
      return;
    }

    setRows(prev => prev.map(r => r._id === row._id ? { ...r, _saving: true, _error: null } : r));

    try {
      let response;
      if (row.entryType === 'sales') {
        response = await saveSales(row);
      } else if (row.entryType === 'expenses') {
        response = await saveExpenses(row);
      } else if (row.entryType === 'payments') {
        response = await savePayments(row);
      } else if (row.entryType === 'stock') {
        response = await saveStock(row);
      } else {
        throw new Error('Unknown entry type');
      }

      // All our APIs return { success: true/false }.
      // Treat any response with success === true as success,
      // or responses without a success field but no error (legacy shape).
      const isSuccess = response
        && response.success === true;

      if (isSuccess) {
        await loadEntries(selectedDate);
        // Move saved entry into the read-only section above; leave a fresh blank row
        setRows(prev => {
          const rest = prev.filter(r => r._id !== row._id);
          return [...rest, blankRow(nextId())];
        });
      } else {
        const msg = (response && (response.error || response.message)) || 'Server rejected the entry. Check all required fields.';
        console.error('QuickEntry save failed:', response);
        throw new Error(msg);
      }
    } catch (err) {
      console.error('QuickEntry saveRow error:', err);
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, _saving: false, _error: err.message } : r));
    }
  };

  // ── Client-side validation ──────────────────────────────────────────────────
  const validateRow = (row) => {
    const { entryType, subType, amount } = row;
    if (!selectedDate) return 'Date is required.';
    if (!amount || parseFloat(amount) <= 0) return 'Amount must be greater than 0.';

    if (entryType === 'expenses') {
      if (subType === 'expense') {
        if (!row.expenseType) return 'Expense Type is required.';
        if (!row.supplier) return 'Supplier is required.';
        if (!row.paymentMethod) return 'Payment Method is required.';
      }
      if (subType === 'overtime') {
        if (!row.workerName) return 'Worker Name is required.';
      }
    }

    if (entryType === 'payments') {
      if (!row.paymentMethod) return 'Payment Method is required.';
      if (subType === 'company' && !row.customerCompany) return 'Company is required.';
      if (subType === 'supplier' && !row.supplier) return 'Supplier is required.';
      if (subType === 'walkin' && !row.customerCompany) return 'Customer is required.';
      if (subType === 'overtime' && !row.workerName) return 'Worker Name is required.';
    }

    if (entryType === 'stock') {
      if (!row.stockType) return 'Stock Category (Oil/Filter) is required.';
      if (!row.quantity || parseFloat(row.quantity) <= 0) return 'Quantity must be greater than 0.';
      if (subType === 'addStock' && !row.supplier) return 'Supplier is required.';
      if (subType === 'sellCredit' && !row.customerCompany && !row._selectedCompanyId) {
        return 'Company is required for credit stock sale.';
      }
    }

    if (entryType === 'sales') {
      if (!row.vehicleNo) return 'Vehicle No is required.';
      if (!row.paymentMethod) return 'Payment Method is required.';
      if (subType === 'credit') {
        if (!row.customerCompany) return 'Company is required.';
        if (!row.contactNo) return 'Contact No is required for credit sales.';
        if (!row.trnNo) return 'TRN No is required for credit sales.';
      }
    }

    return null; // no error
  };

  // ── API submission helpers ──────────────────────────────────────────────────
  const saveSales = async (row) => {
    const isCash = row.subType === 'walkin';
    let serviceId = row.service || '';
    let customServiceName = !!row._otherService;

    if (row._otherService && row.service) {
      const addRes = await fetch(buildApiUrl('api/services/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceName: row.service }),
      });
      const addData = await addRes.json();
      if (!addData.success) throw new Error(addData.error || 'Failed to add custom service.');
      serviceId = addData.serviceId ?? row.service;
      customServiceName = false;
    }

    const services = [{ serviceId, servicePrice: row.amount || '0', customServiceName }];

    const payload = {
      customerType: isCash ? 'normal' : 'company',
      customerName: row.customerCompany || '',
      companyName: !isCash ? (row.customerCompany || '') : undefined,
      contactNo: row.contactNo || '',
      address: '',
      vehicleType: row.vehicleType || '',
      vehicleNo: row.vehicleNo || '',
      services,
      date: selectedDate,
      paymentMethod: row.paymentMethod || (isCash ? 'cash' : 'credit'),
      commission: row.commission || '',
      driverName: row.driverName || '',
      driverNo: row.driverNo || '',
      notes: row.notes || '',
      trnNo: !isCash ? (row.trnNo || '') : undefined,
    };

    const res = await fetch(buildApiUrl('api/serviceTransaction/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  };

  const saveExpenses = async (row) => {
    if (row.subType === 'overtime') {
      const res = await fetch(buildApiUrl('api/overtime/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Date: selectedDate,
          WorkerName: row.workerName || '',
          OvertimeAmount: parseFloat(row.amount) || 0,
          Notes: row.description || '',
        }),
      });
      return res.json();
    }

    if (row._otherExpenseType && row.expenseType) {
      await fetch(buildApiUrl('api/expensetypes/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ TypeName: row.expenseType }),
      });
    }
    if (row._otherSupplier && row.supplier) {
      await fetch(buildApiUrl('api/expenses/suppliers/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ SupplierName: row.supplier }),
      });
    }

    const res = await fetch(buildApiUrl('api/expenses/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Date: selectedDate,
        Description: row.description || `Expense - ${row.expenseType || ''}`,
        ExpenseAmount: parseFloat(row.amount) || 0,
        PaymentMethod: row.paymentMethod || 'cash',
        ExpenseType: row._otherExpenseType ? 'Other' : (row.expenseType || ''),
        NewExpenseType: row._otherExpenseType ? row.expenseType : null,
        SupplierName: row.supplier || '',
      }),
    });
    return res.json();
  };

  const savePayments = async (row) => {
    const subType = row.subType;

    let res;
    if (subType === 'overtime') {
      res = await fetch(buildApiUrl('api/overtime/settlement/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          WorkerName: row.workerName || '',
          amount: parseFloat(row.amount) || 0,
          paymentMethod: row.paymentMethod || 'cash',
          notes: row.notes || '',
          date: selectedDate,
        }),
      });
    } else if (subType === 'walkin') {
      res = await fetch(buildApiUrl('api/customer/payment/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: row.customerCompany || '',
          amount: parseFloat(row.amount) || 0,
          paymentMethod: row.paymentMethod || 'cash',
          notes: row.notes || '',
          date: selectedDate,
        }),
      });
    } else {
      // company or supplier — entityId stored in _entityId when user picks from dropdown
      const entityId = row._entityId || row.customerCompany || row.supplier || '';
      res = await fetch(buildApiUrl(`api/${subType}/payments/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityId,
          amount: parseFloat(row.amount) || 0,
          paymentMethod: row.paymentMethod || 'cash',
          notes: row.notes || '',
          date: selectedDate,
        }),
      });
    }
    return res.json();
  };

  const saveStock = async (row) => {
    if (row.subType === 'addStock') {
      const res = await fetch(buildApiUrl('api/stock/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          datePurchased: selectedDate,
          supplier: row.supplier || '',
          brand: row.brand || '',
          type: row.itemType || '',
          stockType: row.stockType || '',
          quantity: parseFloat(row.quantity) || 0,
          price: parseFloat(row.amount) || 0,
          description: row.description || '',
        }),
      });
      return res.json();
    } else if (row.subType === 'sellCash' || row.subType === 'sellCredit') {
      const isCredit = row.subType === 'sellCredit';
      const res = await fetch(buildApiUrl('api/stock/sell'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isCredit ? {
          date: selectedDate,
          transactionType: 'Credit',
          companyName: row._otherCompany ? row.customerCompany : row._selectedCompanyId,
          contactPerson: row.driverName || '',
          creditContactNumber: row.contactNo || '',
          trnNumber: row.trnNo || '',
          address: '',
          driverName: row.driverName || '',
          driverNumber: row.driverNo || '',
          vehicleType: row.vehicleType || 'Car',
          vehicleNumber: row.vehicleNo || 'N/A',
          paymentMethod: row.paymentMethod || 'Cash',
          stockType: row.stockType || '',
          selectedBrand: row.brand || '',
          selectedType: row.itemType || '',
          quantity: parseFloat(row.quantity) || 0,
          amount: parseFloat(row.amount) || 0,
          notes: row.notes || '',
        } : {
          date: selectedDate,
          transactionType: 'Cash',
          customerName: row.customerCompany || 'Walk-in Customer',
          contactNumber: row.contactNo || '0000000000',
          address: '',
          vehicleType: row.vehicleType || 'Car',
          vehicleNumber: row.vehicleNo || 'N/A',
          paymentMethod: row.paymentMethod || 'Cash',
          stockType: row.stockType || '',
          selectedBrand: row.brand || '',
          selectedType: row.itemType || '',
          quantity: parseFloat(row.quantity) || 0,
          amount: parseFloat(row.amount) || 0,
          notes: row.notes || '',
        }),
      });
      return res.json();
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  // Render a cell for a new-entry row
  const renderEntryCell = (row, col) => {
    const { entryType, subType } = row;
    const relevant = isRelevant(col.id, entryType, subType);

    if (!relevant) {
      return <td key={col.id} className="qe-cell-locked" aria-disabled="true" />;
    }

    const highlighted = relevant ? 'qe-cell-highlighted' : '';
    const cellClass = `qe-cell-active ${highlighted}`;

    switch (col.id) {
      case 'entryType':
        return (
          <td key={col.id} className={cellClass}>
            <select
              {...withNav(row._id, col.id, {
                value: entryType,
                onChange: e => updateRow(row._id, 'entryType', e.target.value),
                'aria-label': 'Entry type',
              })}
            >
              <option value="">-- Select --</option>
              <option value="sales">Sales</option>
              <option value="expenses">Expenses</option>
              <option value="payments">Payment</option>
              <option value="stock">Stock</option>
            </select>
          </td>
        );

      case 'subType':
        return (
          <td key={col.id} className={cellClass}>
            {entryType === 'sales' && (
              <select {...withNav(row._id, col.id, {
                value: subType,
                onChange: e => updateRow(row._id, 'subType', e.target.value),
                'aria-label': 'Sale sub-type',
              })}>
                <option value="">-- Select --</option>
                <option value="walkin">Walk-in</option>
                <option value="credit">Credit</option>
              </select>
            )}
            {entryType === 'expenses' && (
              <select {...withNav(row._id, col.id, {
                value: subType,
                onChange: e => updateRow(row._id, 'subType', e.target.value),
                'aria-label': 'Expense sub-type',
              })}>
                <option value="">-- Select --</option>
                <option value="expense">Expense</option>
                <option value="overtime">Overtime Payment</option>
              </select>
            )}
            {entryType === 'payments' && (
              <select {...withNav(row._id, col.id, {
                value: subType,
                onChange: e => updateRow(row._id, 'subType', e.target.value),
                'aria-label': 'Payment sub-type',
              })}>
                <option value="">-- Select --</option>
                <option value="company">Company</option>
                <option value="supplier">Supplier</option>
                <option value="walkin">Walk-in Credit</option>
                <option value="overtime">Overtime</option>
              </select>
            )}
            {entryType === 'stock' && (
              <select {...withNav(row._id, col.id, {
                value: subType,
                onChange: e => updateRow(row._id, 'subType', e.target.value),
                'aria-label': 'Stock sub-type',
              })}>
                <option value="">-- Select --</option>
                <option value="addStock">Add Stock</option>
                <option value="sellCash">Sell Stock (Cash)</option>
                <option value="sellCredit">Sell Stock (Credit)</option>
              </select>
            )}
            {!entryType && <span style={{ color: '#ccc', fontSize: '0.72rem' }}>—</span>}
          </td>
        );

      case 'service':
        return (
          <td key={col.id} className={cellClass}>
            {row._otherService ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.service,
                  onChange: e => updateRow(row._id, 'service', e.target.value),
                  'aria-label': 'Custom service',
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.service,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Others') patchRow(row._id, { _otherService: true, service: '' });
                    else patchRow(row._id, { _otherService: false, service: val });
                  },
                  'aria-label': 'Service',
                })}
              >
                <option value="">Select Service</option>
                {(dropdowns.services || []).map((s, i) => <option key={i} value={s}>{s}</option>)}
                <option value="Others">Others</option>
              </select>
            )}
          </td>
        );

      case 'vehicleNo':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.vehicleNo,
              onChange: e => updateRow(row._id, 'vehicleNo', e.target.value),
              'aria-label': 'Vehicle number',
            })} />
          </td>
        );

      case 'vehicleType':
        return (
          <td key={col.id} className={cellClass}>
            {row._otherVehicleType ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.vehicleType,
                  onChange: e => updateRow(row._id, 'vehicleType', e.target.value),
                  'aria-label': 'Custom vehicle type',
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.vehicleType,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Others') patchRow(row._id, { _otherVehicleType: true, vehicleType: '' });
                    else patchRow(row._id, { _otherVehicleType: false, vehicleType: val });
                  },
                  'aria-label': 'Vehicle type',
                })}
              >
                <option value="">Select Type</option>
                {(dropdowns.vehicleTypes || []).map((v, i) => <option key={i} value={v}>{v}</option>)}
                <option value="Others">Others</option>
              </select>
            )}
          </td>
        );

      case 'customerCompany': {
        const renderCompanyCredit = () => (
          row._otherCompany ? (
            <input
              {...withNav(row._id, col.id, {
                type: 'text',
                value: row.customerCompany,
                onChange: e => updateRow(row._id, 'customerCompany', e.target.value),
                'aria-label': 'Company name',
              })}
            />
          ) : (
            <select
              {...withNav(row._id, col.id, {
                value: row._selectedCompanyId,
                onChange: e => {
                  const val = e.target.value;
                  if (val === 'Others') {
                    patchRow(row._id, {
                      _otherCompany: true,
                      _selectedCompanyId: '',
                      customerCompany: '',
                      contactNo: '',
                      trnNo: '',
                      driverName: '',
                    });
                  } else {
                    const company = findCompany(val);
                    patchRow(row._id, {
                      _otherCompany: false,
                      _selectedCompanyId: val,
                      customerCompany: company?.name || '',
                      ...applyCompanyAutofill(company),
                    });
                  }
                },
                'aria-label': 'Company',
              })}
            >
              <option value="">Select Company</option>
              {companiesInfo.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="Others">Others</option>
            </select>
          )
        );

        if (entryType === 'sales' && subType === 'credit') {
          return <td key={col.id} className={cellClass}>{renderCompanyCredit()}</td>;
        }
        if (entryType === 'payments' && subType === 'company') {
          return (
            <td key={col.id} className={cellClass}>
              <select
                {...withNav(row._id, col.id, {
                  value: row.customerCompany,
                  onChange: e => {
                    const val = e.target.value;
                    const company = dropdowns.companies.find(c => String(c.id) === val);
                    setRows(prev => prev.map(r => r._id === row._id
                      ? { ...r, customerCompany: val, _entityId: company ? company.id : val }
                      : r
                    ));
                  },
                  'aria-label': 'Company',
                })}
              >
                <option value="">Select Company</option>
                {(dropdowns.companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </td>
          );
        }
        if (entryType === 'payments' && subType === 'walkin') {
          return (
            <td key={col.id} className={cellClass}>
              <select {...withNav(row._id, col.id, {
                value: row.customerCompany,
                onChange: e => updateRow(row._id, 'customerCompany', e.target.value),
                'aria-label': 'Walk-in customer',
              })}>
                <option value="">Select Customer</option>
                {dropdowns.walkinCustomers.map((n, i) => <option key={i} value={n}>{n}</option>)}
              </select>
            </td>
          );
        }
        if (entryType === 'stock' && subType === 'sellCredit') {
          return <td key={col.id} className={cellClass}>{renderCompanyCredit()}</td>;
        }
        if (entryType === 'stock' && subType === 'sellCash') {
          return (
            <td key={col.id} className={cellClass}>
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.customerCompany,
                  onChange: e => updateRow(row._id, 'customerCompany', e.target.value),
                  'aria-label': 'Customer name',
                })}
              />
            </td>
          );
        }
        // Default: free text
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.customerCompany,
              onChange: e => updateRow(row._id, 'customerCompany', e.target.value),
              'aria-label': 'Customer or company',
            })} />
          </td>
        );
      }

      case 'contactNo': {
        const creditLocked = entryType === 'sales' && subType === 'credit' && !row._otherCompany && !!row._selectedCompanyId;
        return (
          <td key={col.id} className={cellClass}>
            <input
              {...withNav(row._id, col.id, {
                type: 'text',
                value: row.contactNo,
                onChange: e => updateRow(row._id, 'contactNo', e.target.value),
                'aria-label': 'Contact number',
                readOnly: creditLocked,
              })}
            />
          </td>
        );
      }

      case 'driverName': {
        const creditLocked = entryType === 'sales' && subType === 'credit' && !row._otherCompany && !!row._selectedCompanyId;
        return (
          <td key={col.id} className={cellClass}>
            <input
              {...withNav(row._id, col.id, {
                type: 'text',
                value: row.driverName,
                onChange: e => updateRow(row._id, 'driverName', e.target.value),
                'aria-label': 'Driver name',
                readOnly: creditLocked,
              })}
            />
          </td>
        );
      }

      case 'driverNo':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.driverNo,
              onChange: e => updateRow(row._id, 'driverNo', e.target.value),
              'aria-label': 'Driver number',
            })} />
          </td>
        );

      case 'trnNo': {
        const creditLocked = entryType === 'sales' && subType === 'credit' && !row._otherCompany && !!row._selectedCompanyId;
        return (
          <td key={col.id} className={cellClass}>
            <input
              {...withNav(row._id, col.id, {
                type: 'text',
                value: row.trnNo,
                onChange: e => updateRow(row._id, 'trnNo', e.target.value),
                'aria-label': 'TRN number',
                readOnly: creditLocked,
              })}
            />
          </td>
        );
      }

      case 'commission':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.commission,
              onChange: e => updateRow(row._id, 'commission', e.target.value),
              'aria-label': 'Commission',
            })} />
          </td>
        );

      case 'paymentMethod': {
        let options = [];
        if (entryType === 'sales') options = ['cash','credit','pos'];
        else if (entryType === 'expenses') options = ['cash','credit','debit','pos'];
        else if (entryType === 'payments') options = ['cash','bank-transfer','cheque','pos'];
        else if (entryType === 'stock' && (subType === 'sellCash' || subType === 'sellCredit')) options = ['Cash','Card','POS'];
        else options = ['cash','bank-transfer','card','pos'];
        return (
          <td key={col.id} className={cellClass}>
            <select {...withNav(row._id, col.id, {
              value: row.paymentMethod,
              onChange: e => updateRow(row._id, 'paymentMethod', e.target.value),
              'aria-label': 'Payment method',
            })}>
              <option value="">Method</option>
              {options.map((o, i) => <option key={i} value={o}>{o === 'pos' || o === 'POS' ? 'POS' : (o.charAt(0).toUpperCase() + o.slice(1))}</option>)}
            </select>
          </td>
        );
      }

      case 'amount':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'number',
              value: row.amount,
              onChange: e => updateRow(row._id, 'amount', e.target.value),
              min: '0',
              step: '0.01',
              'aria-label': 'Amount',
            })} />
          </td>
        );

      case 'expenseType':
        return (
          <td key={col.id} className={cellClass}>
            {row._otherExpenseType ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.expenseType,
                  onChange: e => updateRow(row._id, 'expenseType', e.target.value),
                  'aria-label': 'Custom expense type',
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.expenseType,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Other') patchRow(row._id, { _otherExpenseType: true, expenseType: '' });
                    else patchRow(row._id, { _otherExpenseType: false, expenseType: val });
                  },
                  'aria-label': 'Expense type',
                })}
              >
                <option value="">Select Type</option>
                {dropdowns.expenseTypes.map((t, i) => <option key={i} value={t}>{t}</option>)}
                <option value="Other">Other</option>
              </select>
            )}
          </td>
        );

      case 'supplier': {
        const supplierList = entryType === 'stock' ? (dropdowns.stockSuppliers || []) : (dropdowns.expenseSuppliers || []);
        if (entryType === 'payments' && subType === 'supplier') {
          return (
            <td key={col.id} className={cellClass}>
              <select
                {...withNav(row._id, col.id, {
                  value: row.supplier,
                  onChange: e => {
                    const val = e.target.value;
                    const sup = supplierList.find(s => String(s.id) === val);
                    setRows(prev => prev.map(r => r._id === row._id
                      ? { ...r, supplier: val, _entityId: sup ? sup.id : val }
                      : r
                    ));
                  },
                  'aria-label': 'Supplier',
                })}
              >
                <option value="">Select Supplier</option>
                {supplierList.map(s => (
                  <option key={s.id} value={s.id}>{supplierLabel(s)}</option>
                ))}
              </select>
            </td>
          );
        }
        return (
          <td key={col.id} className={cellClass}>
            {row._otherSupplier ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.supplier,
                  onChange: e => updateRow(row._id, 'supplier', e.target.value),
                  'aria-label': 'Custom supplier',
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.supplier,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Other') patchRow(row._id, { _otherSupplier: true, supplier: '' });
                    else patchRow(row._id, { _otherSupplier: false, supplier: val });
                  },
                  'aria-label': 'Supplier',
                })}
              >
                <option value="">Select Supplier</option>
                {supplierList.map((s, i) => (
                  <option key={s.id ?? i} value={supplierValue(s)}>{supplierLabel(s)}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            )}
          </td>
        );
      }

      case 'workerName':
        return (
          <td key={col.id} className={cellClass}>
            {row._otherWorker ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.workerName,
                  onChange: e => updateRow(row._id, 'workerName', e.target.value),
                  'aria-label': 'Custom worker name',
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.workerName,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Other') patchRow(row._id, { _otherWorker: true, workerName: '' });
                    else patchRow(row._id, { _otherWorker: false, workerName: val });
                  },
                  'aria-label': 'Worker name',
                })}
              >
                <option value="">Select Worker</option>
                {dropdowns.workers.map((w, i) => <option key={i} value={w}>{w}</option>)}
                <option value="Other">Other</option>
              </select>
            )}
          </td>
        );

      case 'stockType':
        return (
          <td key={col.id} className={cellClass}>
            <select {...withNav(row._id, col.id, {
              value: row.stockType,
              onChange: e => updateRow(row._id, 'stockType', e.target.value),
              'aria-label': 'Stock category',
            })}>
              <option value="">Select</option>
              <option value="Oil">Oil</option>
              <option value="Filter">Filter</option>
            </select>
          </td>
        );

      case 'brand': {
        const brands = row.stockType ? (stockBrands[row.stockType] || []) : [];
        return (
          <td key={col.id} className={cellClass}>
            {row._otherBrand ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.brand,
                  onChange: e => updateRow(row._id, 'brand', e.target.value),
                  'aria-label': 'Custom brand',
                  disabled: !row.stockType,
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.brand,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Other') patchRow(row._id, { _otherBrand: true, brand: '' });
                    else patchRow(row._id, { _otherBrand: false, brand: val });
                  },
                  'aria-label': 'Brand',
                  disabled: !row.stockType,
                })}
              >
                <option value="">Select Brand</option>
                {brands.map((b, i) => <option key={i} value={b}>{b}</option>)}
                <option value="Other">Other</option>
              </select>
            )}
          </td>
        );
      }

      case 'itemType': {
        const isFilter = row.stockType === 'Filter';
        const types = row.stockType ? (stockTypes[row.stockType] || []) : [];
        return (
          <td key={col.id} className={cellClass}>
            {isFilter ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.itemType,
                  onChange: e => updateRow(row._id, 'itemType', e.target.value),
                  'aria-label': 'Filter number',
                })}
              />
            ) : row._otherItemType ? (
              <input
                {...withNav(row._id, col.id, {
                  type: 'text',
                  value: row.itemType,
                  onChange: e => updateRow(row._id, 'itemType', e.target.value),
                  'aria-label': 'Custom oil type',
                  disabled: !row.stockType,
                })}
              />
            ) : (
              <select
                {...withNav(row._id, col.id, {
                  value: row.itemType,
                  onChange: e => {
                    const val = e.target.value;
                    if (val === 'Other') patchRow(row._id, { _otherItemType: true, itemType: '' });
                    else patchRow(row._id, { _otherItemType: false, itemType: val });
                  },
                  'aria-label': 'Oil type',
                  disabled: !row.stockType,
                })}
              >
                <option value="">Select Type</option>
                {types.map((t, i) => <option key={i} value={t}>{t}</option>)}
                <option value="Other">Other</option>
              </select>
            )}
          </td>
        );
      }

      case 'quantity':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'number',
              value: row.quantity,
              onChange: e => updateRow(row._id, 'quantity', e.target.value),
              min: '0',
              step: '1',
              'aria-label': 'Quantity',
            })} />
          </td>
        );

      case 'description':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.description,
              onChange: e => updateRow(row._id, 'description', e.target.value),
              'aria-label': 'Description',
            })} />
          </td>
        );

      case 'notes':
        return (
          <td key={col.id} className={cellClass}>
            <input {...withNav(row._id, col.id, {
              type: 'text',
              value: row.notes,
              onChange: e => updateRow(row._id, 'notes', e.target.value),
              'aria-label': 'Notes',
            })} />
          </td>
        );

      default:
        return <td key={col.id} className="qe-cell-locked" aria-disabled="true" />;
    }
  };

  // Render a saved (read-only) row from server data
  const renderSavedRow = (entry, idx) => {
    const entryType = entry.entryType || '';
    const rawSubType = entry.subType || entry.saleType || entry.expenseSubType || entry.paymentEntityType || entry.stockAction || '';
    const subType = normalizeSubType(entryType, rawSubType);

    const tagClass = `qe-display-tag qe-tag-${entryType}`;
    const subTagClass = `qe-display-tag qe-tag-${subType}`;

    const formatVal = (v) => (v === null || v === undefined || v === '') ? '—' : String(v);
    const formatAmt = (v) => {
      const n = parseFloat(v);
      return isNaN(n) ? '—' : `${n.toFixed(2)}`;
    };

    return (
      <tr
        key={`saved-${entryType}-${entry.id}-${idx}`}
        className={`qe-row-saved${idx === selectedRow ? ' qe-row-highlighted' : ''}`}
        onClick={() => setSelectedRow(idx)}
      >
        <td className="qe-row-num">{idx + 1}</td>
        {COLUMNS.map(col => {
          const rel = isRelevant(col.id, entryType, subType);
          if (!rel) return <td key={col.id} className="qe-cell-locked" aria-disabled="true" />;

          let content = '—';
          switch (col.id) {
            case 'entryType':    content = <span className={tagClass}>{entryType}</span>; break;
            case 'subType':      content = <span className={subTagClass}>{subType}</span>; break;
            case 'service':      content = formatVal(entry.serviceName); break;
            case 'vehicleNo':    content = formatVal(entry.vehicleNo); break;
            case 'vehicleType':  content = formatVal(entry.vehicleType); break;
            case 'customerCompany': content = formatVal(entry.customerCompanyName || entry.entityName || entry.customerName); break;
            case 'contactNo':    content = '—'; break;
            case 'driverName':   content = '—'; break;
            case 'driverNo':     content = '—'; break;
            case 'trnNo':        content = '—'; break;
            case 'commission':   content = formatAmt(entry.commission); break;
            case 'paymentMethod':content = formatVal(entry.paymentMethod); break;
            case 'amount':       content = <span className="qe-display-amount">{formatAmt(entry.amount)}</span>; break;
            case 'expenseType':  content = formatVal(entry.expenseType); break;
            case 'supplier':     content = formatVal(entry.supplierName); break;
            case 'workerName':   content = formatVal(entry.workerName); break;
            case 'stockType':    content = formatVal(entry.stockType); break;
            case 'brand':        content = formatVal(entry.brand); break;
            case 'itemType':     content = formatVal(entry.stockItemType); break;
            case 'quantity':     content = formatVal(entry.quantity); break;
            case 'description':  content = formatVal(entry.description || entry.notes); break;
            case 'notes':        content = formatVal(entry.notes); break;
            default:             content = '—';
          }

          return (
            <td key={col.id}>
              {typeof content === 'string' || typeof content === 'number'
                ? <span className="qe-display-text">{content}</span>
                : content}
            </td>
          );
        })}
        <td><span className="qe-saved-icon" title="Saved">✓</span></td>
      </tr>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="quick-entry-wrapper">
      <h1 className="quick-entry-title">Quick Entry</h1>
      <div className="quick-entry-filter">
        <label htmlFor="qe-date">Select Date:</label>
        <input
          id="qe-date"
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
        />
        <span className="quick-entry-meta">
          {loading ? 'Loading…' : `${savedRows.length} ${savedRows.length === 1 ? 'entry' : 'entries'} on this date`}
        </span>
      </div>

      <div
        className="quick-entry-table-container"
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleTableKeyDown}
      >
        <table className="quick-entry-table" role="grid">
          <thead>
            <tr>
              <th className="col-narrow" scope="col">#</th>
              {COLUMNS.map(col => (
                <th key={col.id} className={col.cls} scope="col" title={col.label}>
                  {col.label}
                </th>
              ))}
              <th className="col-action" scope="col">Save</th>
            </tr>
          </thead>
          <tbody>
            {savedRows.map((entry, idx) => renderSavedRow(entry, idx))}

            {/* ─── New entry rows ─── */}
            {rows.map((row, idx) => (
              <React.Fragment key={row._id}>
                <tr
                  className={`qe-row-entry${row._saved ? ' qe-row-saved' : ''}${row._error ? ' qe-row-error' : ''}`}
                  onClick={() => setSelectedRow(-1)}
                >
                  <td className="qe-row-num">{savedRows.length + idx + 1}</td>
                  {COLUMNS.map(col => renderEntryCell(row, col))}
                  <td className="qe-save-cell">
                    {row._saved
                      ? <span className="qe-saved-icon" title="Saved">✓</span>
                      : (
                        <button
                          className="qe-save-btn"
                          onClick={() => saveRow(row)}
                          disabled={row._saving || !row.entryType || !row.subType}
                          title={row._error || 'Save this row'}
                          aria-label="Save row"
                        >
                          {row._saving ? '…' : '✓'}
                        </button>
                      )
                    }
                  </td>
                </tr>
                {row._error && (
                  <tr className="qe-row-error-banner">
                    <td />
                    <td colSpan={COLUMNS.length + 1}>
                      <span className="qe-error-msg">⚠ {row._error}</span>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {/* Add Row — hidden for now
            <tr className="qe-add-row-row">
              <td colSpan={COLUMNS.length + 2}>
                <button className="qe-add-row-btn" onClick={addRow}>
                  + Add Row
                </button>
              </td>
            </tr>
            */}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuickEntry;
